import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import { useNetworkStore } from "../stores/network";
import { useMyTxs } from "../stores/my-txs";
import { useTxComparison } from "../api/hooks";
import {
  PageContainer,
  PageTitle,
  Card,
  Table,
  TableWrapper,
  Badge,
  Mono,
  Loading,
  Flex,
  Button,
} from "../components/ui";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";
import { TxFingerprint, FingerprintCompare } from "../components/TxFingerprint";
import { HexDisplay } from "../components/HexDisplay";

const TxPills = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

const TxPill = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  border: 1px solid ${(p) => p.color};
  background: ${(p) => p.color}15;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  &:hover {
    color: ${theme.colors.danger};
  }
`;

const FingerprintSection = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const SideBySide = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: stretch;

  ${media.md} {
    flex-direction: column;
  }
`;

const SideBySideLeft = styled(Card)`
  flex: 1;
  padding: ${theme.spacing.md};
  min-width: 0;
`;

const SideBySideRight = styled(Card)`
  flex: 1;
  padding: 0;
  overflow: hidden;
  min-width: 0;
`;

const FingerprintGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.md};
`;

const FingerprintCard = styled(Card)`
  padding: ${theme.spacing.sm};
  text-align: center;
`;

const FingerprintLabel = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing.xs};
  font-family: monospace;
`;

const ComparisonCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const DiffCell = styled.td<{ diff?: boolean }>`
  background: ${(p) => (p.diff ? `${theme.colors.warning}15` : "transparent")};
`;

const FEATURE_LABELS = [
  "Note Hashes",
  "Nullifiers",
  "L2→L1 Msgs",
  "Private Logs",
  "Class Logs",
  "Public Logs",
  "DA Mana Limit",
  "L2 Mana Limit",
  "Fee/DA Mana",
  "Fee/L2 Mana",
  "Setup Calls",
  "App Calls",
  "Teardown",
  "Calldata Size",
  "Expiry Delta",
  "Fee Payer",
];

const COLORS = [
  theme.colors.primary,
  theme.colors.accent,
  theme.colors.success,
  theme.colors.warning,
  theme.colors.danger,
];

function abbreviate(hash: string) {
  return `${hash.slice(0, 8)}…${hash.slice(-4)}`;
}

export function Compare() {
  const { selectedNetwork } = useNetworkStore();
  const { removeFromClipboard, getClipboard } = useMyTxs();
  const [searchParams, setSearchParams] = useSearchParams();
  const clipboard = getClipboard(selectedNetwork);

  // Sync URL ↔ clipboard
  const urlTxs = searchParams.get("txs")?.split(",").filter(Boolean) ?? [];
  const txHashes = urlTxs.length >= 2 ? urlTxs : clipboard;

  useEffect(() => {
    if (txHashes.length >= 2) {
      setSearchParams({ txs: txHashes.join(",") }, { replace: true });
    }
  }, [txHashes.join(",")]);

  const { data, isLoading } = useTxComparison(selectedNetwork, txHashes);

  if (txHashes.length < 2) {
    return (
      <PageContainer>
        <PageTitle>Compare Transactions</PageTitle>
        <Card>
          <p style={{ color: theme.colors.textMuted }}>
            Select at least 2 transactions to compare. Use the <strong>+</strong> button on any
            transaction to add it to the clipboard.
          </p>
        </Card>
      </PageContainer>
    );
  }

  if (isLoading) return <Loading />;

  const txs = data?.transactions ?? [];

  const handleRemove = (hash: string) => {
    removeFromClipboard(selectedNetwork, hash);
    const remaining = txHashes.filter((h) => h !== hash);
    if (remaining.length >= 2) {
      setSearchParams({ txs: remaining.join(",") }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  return (
    <PageContainer>
      <Flex justify="space-between" style={{ marginBottom: theme.spacing.sm }}>
        <PageTitle style={{ marginBottom: 0 }}>Compare Transactions</PageTitle>
      </Flex>

      <TxPills>
        {txHashes.map((hash, i) => (
          <TxPill key={hash} color={COLORS[i % COLORS.length]}>
            {abbreviate(hash)}
            <RemoveBtn onClick={() => handleRemove(hash)}>×</RemoveBtn>
          </TxPill>
        ))}
      </TxPills>

      {txs.length === 2 && txs[0].featureVector && txs[1].featureVector ? (
        /* 2 txs: side-by-side (fingerprint left, table right) */
        <SideBySide>
          <SideBySideLeft>
            <FingerprintCompare
              vectorA={txs[0].featureVector}
              vectorB={txs[1].featureVector}
              colorA={COLORS[0]}
              colorB={COLORS[1]}
              labelA={abbreviate(txs[0].txHash)}
              labelB={abbreviate(txs[1].txHash)}
              showLabels
            />
          </SideBySideLeft>
          <SideBySideRight>
            <ComparisonTable txs={txs} />
          </SideBySideRight>
        </SideBySide>
      ) : (
        <>
          <FingerprintSection>
            <FingerprintGrid>
              {txs.map(
                (tx, i) =>
                  tx.featureVector && (
                    <FingerprintCard key={tx.txHash}>
                      <FingerprintLabel>{abbreviate(tx.txHash)}</FingerprintLabel>
                      <TxFingerprint
                        vector={tx.featureVector}
                        color={COLORS[i % COLORS.length]}
                        showLabels
                      />
                    </FingerprintCard>
                  ),
              )}
            </FingerprintGrid>
          </FingerprintSection>
          <ComparisonCard>
            <ComparisonTable txs={txs} />
          </ComparisonCard>
        </>
      )}
    </PageContainer>
  );
}

function ComparisonTable({
  txs,
}: {
  txs: {
    txHash: string;
    status: string;
    featureVector: (number | string)[] | null;
    clusterId: number | null;
    clusterSize: number | null;
  }[];
}) {
  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <th>Feature</th>
            {txs.map((tx, i) => (
              <th key={tx.txHash} style={{ color: COLORS[i % COLORS.length] }}>
                {abbreviate(tx.txHash)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {txs.length > 0 &&
            txs[0].featureVector &&
            FEATURE_LABELS.map((label, fi) => {
              const values = txs.map((tx) => tx.featureVector?.[fi]);
              const allSame = values.every((v) => v === values[0]);
              return (
                <tr key={fi}>
                  <td style={{ color: theme.colors.textMuted }}>{label}</td>
                  {txs.map((tx) => (
                    <DiffCell key={tx.txHash} diff={!allSame}>
                      <Mono style={{ fontSize: "11px" }}>
                        {fi === 15 ? (
                          <HexDisplay
                            address={String(tx.featureVector?.[fi] ?? "")}
                            mode="label"
                            link={false}
                          />
                        ) : fi === 12 ? (
                          tx.featureVector?.[fi] ? (
                            "Yes"
                          ) : (
                            "No"
                          )
                        ) : typeof tx.featureVector?.[fi] === "number" ? (
                          (tx.featureVector[fi] as number).toLocaleString()
                        ) : (
                          String(tx.featureVector?.[fi] ?? "—")
                        )}
                      </Mono>
                    </DiffCell>
                  ))}
                </tr>
              );
            })}
          <tr>
            <td style={{ color: theme.colors.textMuted }}>Status</td>
            {txs.map((tx) => (
              <td key={tx.txHash}>
                <Badge
                  color={tx.status === "finalized" ? theme.colors.success : theme.colors.accent}
                >
                  {tx.status}
                </Badge>
              </td>
            ))}
          </tr>
          <tr>
            <td style={{ color: theme.colors.textMuted }}>Cluster</td>
            {txs.map((tx) => (
              <td key={tx.txHash}>
                {tx.clusterId === -1 ? (
                  <Badge color={theme.colors.danger}>Outlier</Badge>
                ) : tx.clusterId != null ? (
                  <Badge color={theme.colors.primary}>
                    #{tx.clusterId} ({tx.clusterSize})
                  </Badge>
                ) : (
                  "—"
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </Table>
    </TableWrapper>
  );
}
